import { Injectable, inject } from "@angular/core";
import { FormControl } from "@angular/forms";
import { debounceTime, distinctUntilChanged, startWith, Subject, takeUntil } from "rxjs";
import { ChangeEvent, NotifyEvent, TableCell, TableColumn, TableRow, TableState } from "../../models/common-table";
import { CommonTableFilterService, MultiSelectItem } from "./common-table-filter.service";
import { CommonTablePersistenceService } from "./common-table-persistence.service";
import { CommonTableStateService } from "./common-table-state.service";
import { cloneRowCells } from "../../utils/common-table.utils";

export interface EditModeState {
  editModeRows: number[];
  originalRowValue: TableRow | null;
  editingRowIndex: number | null;
}

export interface ToggleEditModeOptions {
  rowIndex: number;
  currentPage: TableRow[];
  columns: TableColumn[];
  editModeRows: number[];
  originalRowValue: TableRow | null;
  editingRowIndex: number | null;
  destroy$: Subject<void>;
  onCellChange: (event: unknown, data: unknown, column: string, row: TableRow) => void;
}

export interface CellChangeProcessResult<T> {
  shouldReturnEarly: boolean;
  processedData: T;
  areAllRowsSelected: boolean;
}

@Injectable({
  providedIn: "root",
})
export class CommonTableFacadeService {
  private stateService: CommonTableStateService = inject(CommonTableStateService);
  private persistenceService: CommonTablePersistenceService = inject(CommonTablePersistenceService);
  private filterService: CommonTableFilterService = inject(CommonTableFilterService);

  public getTableState(tableName: string): TableState {
    return this.persistenceService.getTableState(tableName);
  }

  public setTableState(
    tableName: string,
    currentPageSize: number,
    selectedColumns: Partial<TableColumn>[],
    updates: Partial<TableState>,
  ): void {
    this.persistenceService.setTableState(tableName, currentPageSize, selectedColumns, updates);
  }

  public applyColumnSelection(columns: TableColumn[], updatedColumns: Partial<TableColumn>[]): string[] {
    const deletedColumnNames: string[] = [];
    const columnsByTitle: Map<string, TableColumn> = new Map<string, TableColumn>(
      columns.map((column) => [column.title, column]),
    );

    for (const updatedColumn of updatedColumns) {
      const column: TableColumn | undefined = updatedColumn.title ? columnsByTitle.get(updatedColumn.title) : undefined;
      if (column && column.selected !== updatedColumn.selected) {
        column.selected = updatedColumn.selected;
      }

      if (!updatedColumn.selected && updatedColumn.title) {
        deletedColumnNames.push(updatedColumn.title);
      }
    }

    return deletedColumnNames;
  }

  public initializeMultiSelectControls(
    rows: TableRow[],
    columns: TableColumn[],
    destroy$: Subject<void>,
    onCellChange: (event: unknown, data: unknown, column: string, row: TableRow) => void,
  ): void {
    for (const row of rows || []) {
      this.initializeRowMultiSelectControls(row, columns, destroy$, onCellChange, true);
    }
  }

  public initializeRowMultiSelectControls(
    row: TableRow,
    columns: TableColumn[],
    destroy$: Subject<void>,
    onCellChange: (event: unknown, data: unknown, column: string, row: TableRow) => void,
    wrapValueEvent: boolean,
  ): void {
    row?.forEach((cell: TableCell<unknown>) => {
      if (cell?.inputType !== "multiSelect" || cell.control) {
        return;
      }

      const multiSelectCell: TableCell<unknown> & {
        list?: MultiSelectItem[];
        originalList?: MultiSelectItem[];
      } = cell;

      if (!multiSelectCell.originalList) {
        multiSelectCell.originalList = [...(multiSelectCell.list || [])];
      }

      cell.control = new FormControl(cell.selected || []);
      cell.searchControl = new FormControl("");

      cell.searchControl.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged(), startWith(""), takeUntil(destroy$))
        .subscribe((searchValue: string) => {
          multiSelectCell.list = this.filterService.filterMultiSelectItems(
            multiSelectCell.originalList || [],
            searchValue,
            (cell.selected as string[]) || [],
          );
        });

      cell.control.valueChanges.pipe(takeUntil(destroy$)).subscribe((selectedValues: string[]) => {
        cell.selected = selectedValues;
        cell.value = selectedValues;
        const currentSearch: string = (cell.searchControl && cell.searchControl.value) || "";
        multiSelectCell.list = this.filterService.filterMultiSelectItems(
          multiSelectCell.originalList || [],
          currentSearch,
          selectedValues,
        );

        const columnTitle: string = this.getColumnTitle(columns, row, cell);
        const eventPayload: unknown = wrapValueEvent ? { value: selectedValues } : selectedValues;
        onCellChange(eventPayload, cell.data, columnTitle, row);
      });
    });
  }

  public toggleEditMode(options: ToggleEditModeOptions): EditModeState {
    const { rowIndex, currentPage, columns, editModeRows, destroy$, onCellChange } = options;
    const wasInEditMode: boolean = editModeRows.includes(rowIndex);

    if (wasInEditMode) {
      return {
        editModeRows: editModeRows.filter((idx) => idx !== rowIndex),
        originalRowValue: null,
        editingRowIndex: null,
      };
    }

    const row: TableRow = currentPage[rowIndex];
    this.initializeRowMultiSelectControls(row, columns, destroy$, onCellChange, false);

    return {
      editModeRows: [...editModeRows, rowIndex],
      originalRowValue: cloneRowCells(row),
      editingRowIndex: rowIndex,
    };
  }

  public getCurrentPage(rows: TableRow[], pageNumber: number, pageSize: number): TableRow[] {
    return this.stateService.getCurrentPage(rows, pageNumber, pageSize);
  }

  public areAllRowsSelected(rows: TableRow[]): boolean {
    return this.stateService.areAllRowsSelected(rows);
  }

  public toggleSelectAllRows(rows: TableRow[]): { areAllRowsSelected: boolean; rowsToEmit: TableRow[] } {
    const nextSelectedValue: boolean = !this.stateService.areAllRowsSelected(rows);

    for (const row of rows) {
      const data: { [key: string]: unknown } | undefined = row.find((cell) => cell.data)?.data;
      if (data) {
        data["$Selected"] = nextSelectedValue;
      }
    }

    return {
      areAllRowsSelected: nextSelectedValue,
      rowsToEmit: nextSelectedValue ? rows : [],
    };
  }

  public cancelRowEdit(
    rowIndex: number,
    currentPage: TableRow[],
    columns: TableColumn[],
    editModeRows: number[],
    originalRowValue: TableRow | null,
    editingRowIndex: number | null,
    destroy$: Subject<void>,
    onCellChange: (event: unknown, data: unknown, column: string, row: TableRow) => void,
  ): { currentPage: TableRow[]; editState: EditModeState } {
    const updatedPage: TableRow[] = [...currentPage];

    if (originalRowValue && editingRowIndex === rowIndex) {
      updatedPage[rowIndex] = cloneRowCells(originalRowValue);
    }

    const editState: EditModeState = this.toggleEditMode({
      rowIndex,
      currentPage: updatedPage,
      columns,
      editModeRows,
      originalRowValue,
      editingRowIndex,
      destroy$,
      onCellChange,
    });

    return { currentPage: updatedPage, editState };
  }

  public processCellChange<T>(
    event: unknown,
    data: T,
    row: TableRow,
    cell: TableCell<unknown>,
    action: string | undefined,
    inEditMode: boolean,
    tableRows: TableRow[],
  ): CellChangeProcessResult<T> {
    if (cell.inputType === "multiSelect" && inEditMode) {
      const selectedValue: unknown = cell.control ? cell.control.value : cell.value;
      cell.selected = selectedValue as string | string[];
      return {
        shouldReturnEarly: true,
        processedData: data,
        areAllRowsSelected: this.stateService.areAllRowsSelected(tableRows),
      };
    }

    if (cell.type === "scheduler" && cell.subtype === "time" && Array.isArray(cell.data) && Array.isArray(cell.value)) {
      const scheduleIndex: number = cell.data.findIndex((schedule: unknown) => schedule === data);
      if (scheduleIndex !== -1 && typeof event === "string") {
        cell.value[scheduleIndex] = event;
      }
    }

    let processedData: T = data;
    if (action && Array.isArray(data)) {
      const normalizedAction: string = action.toLowerCase();
      const matchedItem: { action?: string; enabled?: boolean | string } | undefined = (
        data as Array<{ action?: string; enabled?: boolean | string }>
      ).find((item) => {
        return item?.action?.toLowerCase().includes(normalizedAction);
      });

      if (matchedItem) {
        matchedItem.enabled = "now";
        processedData = matchedItem as unknown as T;
      }
    }

    return {
      shouldReturnEarly: false,
      processedData,
      areAllRowsSelected: this.stateService.areAllRowsSelected(tableRows),
    };
  }

  public createCellChangePayload<T>(
    event: unknown,
    processedData: T,
    column: string,
    options: Record<string, unknown> | null,
    row: TableRow,
    action?: string,
  ): ChangeEvent<T> {
    return {
      event: event as NotifyEvent,
      data: processedData,
      column,
      options,
      row,
      action,
    };
  }

  private getColumnTitle(columns: TableColumn[], row: TableRow, cell: TableCell<unknown>): string {
    const cellIndex: number = row.indexOf(cell);
    return columns[cellIndex]?.title || "";
  }
}
